<template>
  <b-card>
    <slot name="hint" />
    <div class="d-flex justify-content-between align-items-center mb-0">
      <b-card-title class="mb-0">
        Levels Scatter
      </b-card-title>
      <div
        v-if="$slots['header-hint'] || $scopedSlots['header-hint'] || (portfolioId !== 'u')"
        class="d-flex align-items-center flex-shrink-0 ml-1"
      >
        <slot name="header-hint" />
        <b-button
          v-if="portfolioId !== 'u'"
          variant="outline-secondary"
          size="sm"
          class="d-flex align-items-center"
          @click="downloadCsv"
        >
          <b-img
            :src="require('@/assets/images/icons/file-icons/csv.png')"
            height="20"
            width="20"
          />
          <span class="ml-50 font-weight-bold text-body">Download CSV</span>
        </b-button>
      </div>
    </div>
    <div class="font-small-3 mb-1 text-muted">
      Current relative levels of product properties against expected maturity
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-scatter
          ref="chart"
          :key="chartKey"
          :option-data="chartData"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText, BButton, BImg,
} from 'bootstrap-vue'
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from '../../breakdown/charts/offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BButton,
    BImg,
    AppEchartScatter,
    LoadingContainer,
  },
  props: {
    levelsScatterData: {
      type: Object,
      default: null,
    },
    portfolioName: {
      type: String,
      default: '',
    },
    portfolioId: {
      type: [String, Number],
      default: null,
    },
    searchFilter: {
      type: Object,
      default: null,
    },
    productWeightMode: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      noData: false,
      chartKey: 0,
      chartData: {
        legend: {},
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    levelsScatterData: {
      handler(data) {
        this.updateChart(data)
      },
      immediate: true,
    },
    skin() {
      // Re-process data when theme changes
      this.updateChart(this.levelsScatterData)
    },
    loading(isLoading) {
      if (!isLoading) this.scheduleChartClickBinding()
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
    this.scheduleChartClickBinding()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    downloadCsv() {
      this.$emit('download-csv')

      const isUniverse = this.portfolioId === 'u'
      const isVirtual = this.portfolioId === 'v'
      let method
      let params
      if (isUniverse) {
        method = 'downloadCSVUniverseScatter'
        params = {}
      } else if (isVirtual) {
        method = 'downloadCSVVirtualPortfolioScatter'
        params = {
          searchfilter: JSON.stringify(this.searchFilter),
          productweightingmode: this.productWeightMode || 'equal',
        }
      } else {
        method = 'downloadCSVScatter'
        params = {
          portfolioid: this.portfolioId,
          portfolioname: this.portfolioName,
          productweightingmode: this.productWeightMode || 'equal',
        }
      }
      this.$http.get(`lifecycle/public/report.cfc?method=${method}`, { params, responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(response.data)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = url
          // eslint-disable-next-line prefer-destructuring
          a.download = response.headers['content-disposition'].match(/"([^']+)"/)[1]
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
        })
    },
    getDataURL() {
      if (this.loading) return null
      this.updateChart(this.levelsScatterData)
      return this.noData ? null : renderChartToBase64(this.chartData)
    },
    updateChart(data) {
      if (!this.isValidScatterData(data)) {
        this.setNoData()
        return
      }

      try {
        this.processLevelsScatterData(data)
      } catch (err) {
        console.error('Error processing levels scatter chart:', err)
        this.setNoData()
      }
    },
    isValidScatterData(data) {
      const coverScatter = data?.state_coverscatter
      return !!coverScatter && !!coverScatter.options && Array.isArray(coverScatter.data)
    },
    setNoData() {
      this.noData = true
      this.chartData = {
        legend: { show: false },
        grid: {},
        xAxis: {},
        yAxis: {},
        series: [],
      }
      this.chartKey += 1
    },
    processLevelsScatterData(data) {
      const coverScatter = data.state_coverscatter

      // Check if there's actual data
      this.noData = !coverScatter.data?.some(s => (
        Array.isArray(s?.data) && s.data.some(point => Array.isArray(point) && point.length >= 2)
      ))

      const xMin = new Date(coverScatter.options.xmin).getTime()
      const xMax = new Date(coverScatter.options.xmax).getTime()
      if (Number.isNaN(xMin) || Number.isNaN(xMax)) {
        this.setNoData()
        return
      }
      const rangeYears = (xMax - xMin) / (365.25 * 24 * 60 * 60 * 1000)
      const splitNumber = Math.min(4, Math.max(2, Math.floor(rangeYears)))

      const formatLabel = value => {
        const date = new Date(value)
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      }
      const simulatedTicks = Array.from({ length: splitNumber + 1 }, (_, i) => xMin + ((xMax - xMin) * i) / splitNumber)
      const uniqueLabels = new Set(simulatedTicks.map(formatLabel))
      const showEdgeLabels = uniqueLabels.size === 1

      const colors = ['#1a73e8', '#28c76f', '#ea5455', '#82868b', '#00cfe8', '#ff9f43']

      // Convert ApexCharts scatter format to ECharts format
      const echartsSeries = coverScatter.data.filter(Boolean).map((series, index) => ({
        name: series.name,
        type: 'scatter',
        data: (series.data || [])
          .filter(point => Array.isArray(point) && point.length >= 2)
          .map((point, i) => [...point, series.datalabel?.[i] ?? '', series.products?.[i] ?? null]),
        symbolSize: 4,
        cursor: 'pointer',
        itemStyle: {
          color: colors[index % colors.length],
        },
      }))

      // Add red horizontal reference line at y = 100
      const referenceLine = {
        type: 'line',
        data: [[new Date(coverScatter.options.xmin).getTime(), 100], [new Date(coverScatter.options.xmax).getTime(), 100]],
        lineStyle: {
          color: '#ea5455',
          width: 2,
        },
        symbol: 'none',
        silent: true,
        legendHoverLink: false,
      }

      this.chartData = {
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross',
          },
          formatter: p => {
            if (!p || p.seriesType !== 'scatter') return ''
            const label = p.data[2] ? `${p.data[2]}<br/>` : ''
            return `${label}${p.marker} ${p.seriesName}: ${p.value[1]}`
          },
        },
        legend: {
          show: true,
          left: 0,
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 70,
          top: 60,
          containLabel: false,
        },
        xAxis: {
          type: 'time',
          name: 'Date',
          nameLocation: 'middle',
          nameGap: 35,
          min: coverScatter.options.xmin,
          max: coverScatter.options.xmax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          splitNumber,
          axisLabel: {
            fontSize: 12,
            showMinLabel: showEdgeLabels,
            showMaxLabel: showEdgeLabels,
            formatter: (() => {
              let seen = {}
              let lastValue = -Infinity
              return value => {
                if (value <= lastValue) seen = {}
                lastValue = value
                const date = new Date(value)
                const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
                if (!seen[label]) {
                  seen[label] = true
                  return label
                }
                return ''
              }
            })(),
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Rebased underlying level',
          nameLocation: 'middle',
          nameGap: 50,
          min: coverScatter.options.ymin,
          max: coverScatter.options.ymax,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          scale: true,
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [...echartsSeries, referenceLine],
      }
      this.chartKey += 1
      this.scheduleChartClickBinding()
    },
    scheduleChartClickBinding() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.resizeChart()
          this.bindChartClick()
        }, 100)
      })
    },
    bindChartClick() {
      const chartInstance = this.$refs.chart && this.$refs.chart.getChartInstance && this.$refs.chart.getChartInstance()
      if (chartInstance) {
        chartInstance.off('click')
        chartInstance.on('click', params => {
          if (params.seriesType === 'scatter' && params.data) {
            const reportNumber = params.data[3]
            if (reportNumber) {
              this.$router.push({ name: 'product-report', params: { reportNumber } })
            }
          }
        })
      }
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
